import { UserAvatar } from "@/components/ui/stacked-avatars";
import { cn } from "@/lib/utils";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

interface NoteViewProps {
  content?: string;
  createdAt?: Date | string;
  creatorAvatarUrl?: string;
  creatorName?: string;
  editMode?: boolean;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  onContentChange?: (content: string) => void;
}

const formatTimestamp = (date: Date | string): string => {
  const dateTime = DateTime.fromJSDate(new Date(date));
  const now = DateTime.now();

  const isToday = dateTime.hasSame(now, "day");
  const isYesterday = dateTime.hasSame(now.minus({ days: 1 }), "day");
  const daysAgo = Math.floor(
    now.startOf("day").diff(dateTime.startOf("day"), "days").days,
  );

  if (isToday) {
    return `Today, ${dateTime.toFormat("h:mma").replace("AM", "am").replace("PM", "pm")}`;
  }

  if (isYesterday) {
    return `Yesterday, ${dateTime.toFormat("h:mma").replace("AM", "am").replace("PM", "pm")}`;
  }

  if (daysAgo < 7) {
    // Returns day of week, e.g., Monday, 9:00am
    return `${dateTime.toFormat("cccc")}, ${dateTime.toFormat("h:mma").replace("AM", "am").replace("PM", "pm")}`;
  }

  // More than 7 days ago: Date, 9:00am (e.g., Apr 18, 9:00am)
  return `${dateTime.toFormat("MMM d")}, ${dateTime.toFormat("h:mma").replace("AM", "am").replace("PM", "pm")}`;
};

export function NoteView({
  content: initialContent = "",
  createdAt,
  creatorAvatarUrl,
  creatorName,
  editMode = false,
  placeholder = "Start writing your note...",
  maxLength = 620,
  className,
  onContentChange,
}: NoteViewProps) {
  const [content, setContent] = useState(initialContent);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isCreating = !createdAt && editMode;
  const displayAvatarUrl = creatorAvatarUrl;
  const displayName = creatorName;

  useEffect(() => {
    if (editMode && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length,
      );
    }
  }, [editMode]);

  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  return (
    <div
      className={cn(
        "relative rounded-sm bg-surfaces-z3 p-4 shadow-sm",
        className,
      )}
    >
      <div className="mb-5 flex items-center justify-between">
        <UserAvatar
          imageUrl={displayAvatarUrl}
          name={displayName ?? ""}
          size={40}
          showBorder={true}
        />
        {!editMode && createdAt && (
          <span className="text-sm text-secondary">
            {formatTimestamp(createdAt)}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <TextareaAutosize
          ref={textareaRef}
          value={content || ""}
          onChange={(e) => {
            if (editMode || isCreating) {
              const newContent = e.target.value.slice(0, maxLength);
              setContent(newContent);
              onContentChange?.(newContent);
            }
          }}
          placeholder={placeholder}
          maxLength={maxLength}
          readOnly={!editMode && !isCreating}
          className={cn(
            "w-full resize-none border-0 bg-transparent p-0 font-mono text-sm",
            "text-primary placeholder:text-tertiary",
            "focus:outline-none focus:ring-0",
            !editMode && !isCreating && "cursor-default",
          )}
          minRows={20}
          maxRows={20}
          disabled={!editMode && !isCreating}
        />
        {(editMode || isCreating) && (
          <div className="flex items-center">
            <span className="text-sm text-tertiary">
              Character Limit: {content.length} / {maxLength}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
